import { SIGNAL_TYPE_REGISTRY } from '@parcel/types';
import { Badge, type BadgeTone, Skeleton } from '@parcel/ui';
import {
  Building2,
  FileSignature,
  FileText,
  Hammer,
  Landmark,
  Receipt,
  Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { normalizeExternalUrl } from '../../lib/external-url.js';
import { logoUrl } from '../../lib/logo.js';
import { type Stage, stageMeta } from '../../lib/projects-display.js';
import { formatSignalCalendarDate } from '../../lib/signal-dates.js';
import type { ApiSignalSummary, ApiSignalType } from '../../lib/signals.js';
import { SignalGateOverlay } from '../billing/signal-gate-overlay.js';
import { EntityAvatar } from './entity-drawer-shell.js';

interface SignalFeedProps {
  signals: ApiSignalSummary[];
  isLoading?: boolean;
  withheld?: { count: number; oldestFiledAt: string | null };
  workspaceSlug?: string | null;
}

type SignalTone = 'amber' | 'blue' | 'green' | 'purple' | 'red' | 'gray';

interface SignalTypeMeta {
  label: string;
  icon: LucideIcon;
  /** Stage-bearing signals carry the exact stage tone so the badge matches the stage. */
  tone?: BadgeTone;
  /** Stock variant fallback for non-stage signals (denial, withdrawal, rfp, …). */
  variant: SignalTone;
}

// Presentation only (tone + icon). The human LABEL is sourced from the canonical
// SIGNAL_TYPE_REGISTRY (@parcel/types) in getSignalMeta, so signal-type names are
// identical across Notifications, the detail panel, and every drawer feed.
interface SignalTypePresentation {
  tone: SignalTone;
  icon: LucideIcon;
}

const SIGNAL_TYPE_PRESENTATION: Record<ApiSignalType, SignalTypePresentation> = {
  project_announcement: { tone: 'blue', icon: Sparkles },
  pre_application: { tone: 'blue', icon: FileSignature },
  site_acquisition: { tone: 'amber', icon: Receipt },
  demolition_permit: { tone: 'amber', icon: Hammer },
  site_prep: { tone: 'purple', icon: Hammer },
  entitlement_application: { tone: 'blue', icon: Landmark },
  environmental_review: { tone: 'purple', icon: FileSignature },
  public_review: { tone: 'purple', icon: FileSignature },
  rfp: { tone: 'gray', icon: FileText },
  entitlement_approval: { tone: 'green', icon: Landmark },
  entitlement_denial: { tone: 'red', icon: Landmark },
  withdrawal: { tone: 'gray', icon: FileText },
  expiration: { tone: 'gray', icon: FileText },
  permit_filed: { tone: 'blue', icon: FileText },
  permit_issued: { tone: 'green', icon: FileText },
  construction_loan: { tone: 'purple', icon: Receipt },
  under_construction: { tone: 'amber', icon: Hammer },
  team_update: { tone: 'blue', icon: Sparkles },
  completion: { tone: 'green', icon: Sparkles },
};

// Stage color override for stage-bearing signal types, mirrored from the
// canonical registry's `stage` field (SIGNAL_TYPE_REGISTRY); a signal-feed test
// asserts the two stay in sync. Null-stage types (permit_filed, the terminal
// dispositions, team_update, rfp) fall back to their base presentation tone.
export const SIGNAL_STAGE_BY_TYPE: Partial<Record<ApiSignalType, Stage>> = {
  project_announcement: 'pre_filing',
  pre_application: 'pre_filing',
  site_acquisition: 'pre_filing',
  demolition_permit: 'permitted',
  site_prep: 'permitted',
  entitlement_application: 'filed',
  environmental_review: 'filed',
  public_review: 'under_review',
  entitlement_approval: 'approved',
  permit_issued: 'permitted',
  construction_loan: 'permitted',
  under_construction: 'under_construction',
  completion: 'completed',
};

const DEFAULT_PRESENTATION: SignalTypePresentation = { tone: 'gray', icon: Sparkles };

export function getSignalMeta(signalType: ApiSignalType): SignalTypeMeta {
  const presentation = SIGNAL_TYPE_PRESENTATION[signalType] ?? DEFAULT_PRESENTATION;
  // Canonical label — one vocabulary across the queue, panel, and feeds.
  const label = SIGNAL_TYPE_REGISTRY[signalType]?.label ?? 'Signal';
  // Stage-bearing types take the EXACT stage tone so the signal badge matches its
  // stage badge; non-stage types (denial, withdrawal, rfp, …) keep a stock variant.
  const matchingStage = SIGNAL_STAGE_BY_TYPE[signalType];
  const tone = matchingStage ? stageMeta(matchingStage).tone : undefined;

  return { label, icon: presentation.icon, tone, variant: presentation.tone };
}

interface SignalActivityCopy {
  title: string;
  subtitle: string;
}

// The headline is the project; the signal type is conveyed by the registry-label
// badge rendered beside it. (Actor-attributed phrasing is a deferred enrichment.)
export function buildSignalActivityCopy(signal: ApiSignalSummary): SignalActivityCopy {
  return {
    title: signal.project_name ?? signal.project_primary_address,
    subtitle: signal.source_display_name ?? '',
  };
}

export function getSignalAvatarLogoDomain(signal: ApiSignalSummary): string | null {
  return signal.source_logo_domain ?? null;
}

function formatDiscoveredDate(iso: string) {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';

  // Always include the year so older (and bad future-dated) signals are
  // unambiguous; the feed often spans more than the current year.
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function groupSignals(signals: ApiSignalSummary[]) {
  const groups = new Map<string, ApiSignalSummary[]>();
  for (const signal of signals) {
    const label = formatSignalCalendarDate(signal.filed_at);
    groups.set(label, [...(groups.get(label) ?? []), signal]);
  }
  return Array.from(groups.entries());
}

function formatCostUsd(value: number | null): string | null {
  if (value == null) return null;
  if (value >= 1_000_000)
    return `$${(value / 1_000_000).toLocaleString('en-US', { maximumFractionDigits: 1 })}M`;
  if (value >= 1_000)
    return `$${(value / 1_000).toLocaleString('en-US', { maximumFractionDigits: 0 })}K`;
  return `$${value.toLocaleString('en-US')}`;
}

function formatFloorArea(value: number | null): string | null {
  if (value == null) return null;
  if (value >= 1_000)
    return `${(value / 1_000).toLocaleString('en-US', { maximumFractionDigits: 0 })}k sq ft`;
  return `${value.toLocaleString('en-US')} sq ft`;
}

function SignalFeedSkeleton() {
  return (
    <div className="space-y-7">
      {['group-a', 'group-b'].map((groupKey) => (
        <section key={groupKey} className="space-y-4">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 text-xs font-medium text-muted-foreground">
            <div className="h-px bg-border" />
            <Skeleton className="h-3 w-12" />
            <div className="h-px bg-border" />
          </div>
          <div className="space-y-6">
            {['row-a', 'row-b'].map((rowKey) => (
              <div
                key={`${groupKey}-${rowKey}`}
                className="grid grid-cols-[2rem_minmax(0,1fr)] gap-4"
              >
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="space-y-3">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-20 w-full bg-muted/60" />
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export function SignalFeed({ signals, isLoading, withheld, workspaceSlug }: SignalFeedProps) {
  if (isLoading) {
    return <SignalFeedSkeleton />;
  }

  if (signals.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex h-32 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
          No signals yet
        </div>
        {workspaceSlug ? (
          <SignalGateOverlay variant="pill" withheld={withheld} workspaceSlug={workspaceSlug} />
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-7">
      {groupSignals(signals).map(([dateLabel, items]) => (
        <section key={dateLabel} className="space-y-4">
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 text-xs font-medium text-muted-foreground">
            <div className="h-px bg-border" />
            <div>{dateLabel}</div>
            <div className="h-px bg-border" />
          </div>
          <div className="relative space-y-6 before:absolute before:left-4 before:top-6 before:h-[calc(100%-3rem)] before:w-px before:bg-border">
            {items.map((signal) => (
              <SignalFeedRow key={signal.id} signal={signal} />
            ))}
          </div>
        </section>
      ))}
      {workspaceSlug ? (
        <SignalGateOverlay variant="pill" withheld={withheld} workspaceSlug={workspaceSlug} />
      ) : null}
    </div>
  );
}

function SignalFeedRow({ signal }: { signal: ApiSignalSummary }) {
  const meta = getSignalMeta(signal.signal_type);
  const SignalIcon = meta.icon;
  const projectLabel = signal.project_name ?? signal.project_primary_address;
  const activity = buildSignalActivityCopy(signal);
  const contentRows = buildSignalContentRows(signal);
  const sourceUrl = normalizeExternalUrl(signal.source_url);
  const showDetailPanel = shouldRenderSignalDetailPanel(signal);

  return (
    <article className="relative grid grid-cols-[2rem_minmax(0,1fr)] gap-4">
      <div className="relative z-10">
        <EntityAvatar
          src={logoUrl(getSignalAvatarLogoDomain(signal))}
          alt={signal.source_display_name || projectLabel}
          fallbackIcon={Building2}
          size="sm"
          href={normalizeExternalUrl(signal.source_url)}
        />
      </div>
      <div className="min-w-0 space-y-3 pb-1">
        <div className="flex min-w-0 items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2 text-sm leading-5">
              <SignalIcon className="h-4 w-4 text-muted-foreground" />
              <span className="min-w-0 truncate font-medium text-foreground">{activity.title}</span>
            </div>
            {signal.summary ? (
              <p className="mt-1 text-sm leading-relaxed text-foreground">{signal.summary}</p>
            ) : null}
            <div className="mt-1 truncate text-sm text-muted-foreground">{activity.subtitle}</div>
          </div>
          <Badge tone={meta.tone} variant={meta.variant} className="shrink-0 whitespace-nowrap">
            {meta.label}
          </Badge>
        </div>

        {showDetailPanel ? (
          <div className="rounded-md border bg-muted/20 px-4 py-3">
            <div className="flex items-start justify-between gap-4">
              {contentRows.length ? (
                <div className="grid flex-1 grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                  {contentRows.map((row) => (
                    <div key={`${signal.id}-${row.label}`} className="min-w-0">
                      <div className="truncate text-xs font-medium text-muted-foreground">
                        {row.label}
                      </div>
                      <div className="mt-1 truncate text-sm text-foreground">{row.value}</div>
                    </div>
                  ))}
                </div>
              ) : null}
              {sourceUrl ? (
                <a
                  href={sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 text-xs font-medium text-primary underline-offset-2 hover:underline"
                >
                  View source
                </a>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </article>
  );
}

export interface SignalContentRow {
  label: string;
  value: string;
}

export function buildSignalContentRows(signal: ApiSignalSummary): SignalContentRow[] {
  const contentRows: SignalContentRow[] = [];
  const cost = formatCostUsd(signal.reported_cost_usd);
  const area = formatFloorArea(signal.reported_gross_floor_area_sf);
  if (cost) contentRows.push({ label: 'Reported cost', value: cost });
  if (area) contentRows.push({ label: 'Floor area', value: area });
  if (signal.source_filing_id)
    contentRows.push({ label: 'Filing ID', value: signal.source_filing_id });
  contentRows.push({ label: 'Discovered', value: formatDiscoveredDate(signal.discovered_at) });
  return contentRows;
}

export function shouldRenderSignalDetailPanel(signal: ApiSignalSummary) {
  return (
    buildSignalContentRows(signal).length > 0 || Boolean(normalizeExternalUrl(signal.source_url))
  );
}
